import type { SelectedTheme } from "@revolt/state/stores/Theme";

import type { RadiusKey } from "./keys";
import { OCEANO, PRESETS } from "./presets";
import { THEME_DEFINITION_VERSION, ThemeDefinition } from "./types";

/**
 * Values shared by every theme until presets override them
 */
export const BASE_DEFINITION: Omit<
  ThemeDefinition,
  "meta" | "darkMode" | "colors" | "typography" | "spacing" | "effects"
> & {
  colors: Pick<ThemeDefinition["colors"], "presence">;
  typography: Pick<ThemeDefinition["typography"], "emoji">;
  spacing: Pick<ThemeDefinition["spacing"], "scale" | "layout">;
} = {
  version: THEME_DEFINITION_VERSION,
  colors: {
    presence: {
      online: "#3ABF7E",
      idle: "#F39F00",
      busy: "#F84848",
      focus: "#4799F0",
      invisible: "#A5A5A5",
    },
  },
  gradients: {},
  backgrounds: {},
  typography: {
    emoji: {
      inline: "1.4em",
      medium: "48px",
      large: "96px",
    },
  },
  radius: {
    // Material 3 Expressive ten-level shape scale
    // https://m3.material.io/styles/shape/corner-radius-scale
    none: "0px",
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    li: "20px",
    xl: "28px",
    xli: "32px",
    xxl: "48px",
    full: "calc(infinity * 1px)",
    circle: "100%",
  },
  spacing: {
    scale: {
      none: "0",
      xxs: "1px",
      xs: "2px",
      s: "6px",
      sm: "4px",
      md: "8px",
      l: "12px",
      lg: "15px",
      x: "28px",
      xl: "32px",
      xxl: "64px",
    },
    layout: {
      "width-channel-sidebar": "248px",
      "width-user-context-menu-truncate": "300px",
      "height-message-box": "32vh",
    },
  },
  borders: {
    width: "1px",
  },
  shadows: {
    "0": "none",
    "1": "0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px 1px rgba(0, 0, 0, 0.15)",
    "2": "0 1px 2px rgba(0, 0, 0, 0.3), 0 2px 6px 2px rgba(0, 0, 0, 0.15)",
    "3": "0 1px 3px rgba(0, 0, 0, 0.3), 0 4px 8px 3px rgba(0, 0, 0, 0.15)",
    "4": "0 2px 3px rgba(0, 0, 0, 0.3), 0 6px 10px 4px rgba(0, 0, 0, 0.15)",
  },
  motion: {
    fast: ".1s ease-in-out",
    medium: ".2s ease",
    scale: 1,
  },
  components: {},
};

/**
 * Build a theme definition from the user's current theme settings
 * @param theme Selected theme from the store
 * @param wallpaperUrl URL of the user's wallpaper, if any
 * @returns Theme definition
 */
export function definitionFromSelectedTheme(
  theme: SelectedTheme,
  wallpaperUrl?: string,
): ThemeDefinition {
  const preset = PRESETS[theme.presetId] ?? OCEANO;

  return {
    ...BASE_DEFINITION,
    meta: {
      id: preset.id,
      name: preset.id,
    },
    darkMode: theme.darkMode,
    radius: scaleRadius(
      BASE_DEFINITION.radius,
      (preset.radiusScale ?? 1) * (theme.radiusScale / 100),
    ),
    shadows: {
      ...BASE_DEFINITION.shadows,
      ...preset.shadows,
    },
    gradients: {
      ...BASE_DEFINITION.gradients,
      ...preset.gradients,
    },
    components: {
      ...BASE_DEFINITION.components,
      ...preset.components,
    },
    colors: {
      ...BASE_DEFINITION.colors,
      source: theme.accent,
      accent: preset.accent,
      surfaceChroma: preset.surfaceChroma,
      contrast: theme.contrast,
      variant: theme.variant,
    },
    typography: {
      ...BASE_DEFINITION.typography,
      ui: theme.interfaceFont,
      mono: theme.monospaceFont,
      messageSize: theme.messageSize,
      tracking: preset.tracking,
    },
    spacing: {
      ...BASE_DEFINITION.spacing,
      messageGroup: theme.messageGroupSpacing,
    },
    effects: {
      blur: theme.blur,
    },
    motion: motionFor(theme.motionLevel),
    wallpaper: wallpaperUrl
      ? {
          url: wallpaperUrl,
          dim: theme.wallpaperDim,
          blur: theme.wallpaperBlur,
          panelOpacity: theme.panelOpacity,
        }
      : undefined,
  };
}

/**
 * Motion timings for an animation level
 * @param level Effective animation level
 * @returns Motion settings
 */
export function motionFor(
  level: SelectedTheme["motionLevel"],
): ThemeDefinition["motion"] {
  if (level === "full") return BASE_DEFINITION.motion;

  const scale = level === "reduced" ? 0.5 : 0;
  return {
    fast: `${0.1 * scale}s ease-in-out`,
    medium: `${0.2 * scale}s ease`,
    scale,
  };
}

/**
 * Multiply every pixel radius by a factor (full and circle stay as they are)
 * @param radius Radius scale
 * @param factor Multiplier
 * @returns Scaled radius scale
 */
export function scaleRadius(
  radius: ThemeDefinition["radius"],
  factor: number,
): ThemeDefinition["radius"] {
  if (factor === 1) return radius;

  return Object.fromEntries(
    Object.entries(radius).map(([key, value]) => {
      const px = /^(\d+(?:\.\d+)?)px$/.exec(value);
      return [
        key,
        px ? `${Math.round(parseFloat(px[1]) * factor * 10) / 10}px` : value,
      ];
    }),
  ) as Record<RadiusKey, string>;
}
